import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-smooth focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-soft hover:shadow-medium",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-muted/80 hover:text-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 shadow-soft",
        ghost: "hover:bg-muted/80 hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        hero: "bg-gradient-hero text-white hover:shadow-large transition-bounce shadow-medium font-semibold",
        family: "bg-gradient-secondary text-white hover:bg-secondary-light shadow-soft hover:shadow-medium",
        // Section-specific variants
        tasks: "bg-secondary text-secondary-foreground hover:bg-secondary hover:brightness-90 shadow-soft", // Green for tasks
        calendar: "bg-accent text-accent-foreground hover:bg-accent hover:brightness-90 shadow-soft", // Yellow for calendar  
        persons: "bg-coral text-coral-foreground hover:bg-coral hover:brightness-90 shadow-soft", // Red for family/persons
        documents: "bg-violet-pastel text-violet-pastel-foreground hover:bg-violet-pastel/90 shadow-soft", // Purple for documents
        // Secondary variants for each section (transparent with colored hover)
        "tasks-secondary": "border border-secondary/20 bg-transparent text-secondary hover:bg-secondary/10 hover:text-secondary-foreground",
        "calendar-secondary": "border border-accent/20 bg-transparent text-accent hover:bg-accent/10 hover:text-accent-foreground",
        "persons-secondary": "border border-coral/20 bg-transparent text-coral hover:bg-coral/10 hover:text-coral-foreground",
        "cancel-hover": "border border-input bg-background hover:bg-muted hover:text-muted-foreground", // Light gray for cancel buttons
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-10 text-lg",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
